import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { DROPDOWN_CONFIG, DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';
import CashSummaryReportHeader from './CashSummaryReportHeader';
import './CashSummaryReport.scss';

// Dummy restaurants — replace with API/Redux data
const RESTAURANTS = [
    { label: 'Chenthoor Restaurant', value: 'Chenthoor Restaurant' },
    { label: 'Chenthoor Restaurant - 2', value: 'Chenthoor Restaurant - 2' },
];

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const pad = (n) => String(n).padStart(2, '0');
const formatDate = (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
const formatNum = (v) => (v === null || v === undefined || v === '' ? '' : Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

// Dummy cash summary — replace with API data.
// Receipts side: opening cash + sales (+ excess). Payments side: GPay, expenses, settlements, closing cash.
// Closing cash is derived so that both sides always balance.
function buildSummary(date) {
    const seed = date.getDate() * 37 + (date.getMonth() + 1) * 11;
    const openingCash = 2000 + ((seed * 7) % 6000);
    const billFrom = 500 + (seed % 100);
    const billTo = billFrom + 20 + (seed % 40);
    const sales = round2(12000 + ((seed * 913) % 20000));
    const excess = 0;
    const gpay = round2(Math.floor(sales * 0.5));
    const expenses = round2(1500 + ((seed * 31) % 2000));
    const settled = round2(Math.floor((sales * 0.3) / 100) * 100);
    const receipts = round2(openingCash + sales + excess);
    const closingCash = round2(receipts - gpay - expenses - settled);
    return {
        date,
        openingCash,
        billFrom,
        billTo,
        sales,
        excess,
        gpay,
        expenses,
        closingCash,
        settled,
        settledTo: 'SADAK THAMBI KAKA',
        totalLeft: receipts,
        totalRight: round2(gpay + expenses + closingCash + settled),
    };
}

class CashSummaryReport extends Component {
    state = {
        Restaurant: RESTAURANTS[0].value,
        reportDate: new Date(),
        summary: null,
        searched: false,
        btnLoading: null, // key of the action currently running: search | showAll | clear | pdf | excel
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    
    componentWillUnmount() {
        this.unmounted = true;
    }

    // Same loader as the Daily Sales Report — full-page .page-loader overlay until the action completes.
    runWithLoader = (key, action) => {
        if (this.state.btnLoading) return;
        this.setState({ btnLoading: key }, () => {
            setTimeout(() => {
                try {
                    action();
                } catch (err) {
                    this.showError(err && err.message ? err.message : 'Something went wrong');
                } finally {
                    if (!this.unmounted) this.setState({ btnLoading: null });
                }
            }, 300);
        });
    };

    showError = (detail) => this.growl && this.growl.show({ severity: 'error', summary: 'Error', detail });

    handleSearch = () => this.runWithLoader('search', this.handleSearchAction);
    handleShowAll = () => this.runWithLoader('showAll', this.handleShowAllAction);
    handleClear = () => this.runWithLoader('clear', this.handleClearAction);
    handleExportExcel = () => this.runWithLoader('excel', this.handleExportExcelAction);
    handleExportPdf = () => this.runWithLoader('pdf', this.handleExportPdfAction);

    handleSearchAction = () => {
        const { reportDate, Restaurant } = this.state;
        if (!Restaurant) return this.showError('Please select Restaurant');
        if (!reportDate) return this.showError('Please select Date');
        this.setState({ summary: buildSummary(reportDate), searched: true });
    };

    handleShowAllAction = () => {
        const now = new Date();
        this.setState({ reportDate: now, summary: buildSummary(now), searched: true });
    };

    handleClearAction = () => {
        this.setState({ Restaurant: RESTAURANTS[0].value, reportDate: new Date(), summary: null, searched: false });
    };

    reportTitle = () => `CASH SUMMARY  AS ON ${formatDate(this.state.reportDate)}`;

    // Left / right ledger rows: [label, amount]
    buildLedger = () => {
        const s = this.state.summary;
        const left = [
            ['OPENING CASH', s.openingCash],
            [`SALES INV ${s.billFrom} TO ${s.billTo}`, s.sales],
            ['', ''],
            ['EXCESS', s.excess],
        ];
        const right = [
            ['GPAY', s.gpay],
            ['EXPENSES', s.expenses],
            ['', ''],
            ['CLOSING CASH', s.closingCash],
            ['', ''],
            [`AMOUNT SETTLED TO ${s.settledTo}`, s.settled],
        ];
        const rows = Math.max(left.length, right.length);
        const matrix = [];
        for (let i = 0; i < rows; i++) {
            const l = left[i] || ['', ''];
            const r = right[i] || ['', ''];
            matrix.push([i === 0 ? formatDate(s.date) : '', l[0], l[1], r[0], r[1]]);
        }
        return matrix;
    };

    handleExportExcelAction = () => {
        if (!this.state.summary) return this.showError('No data to export');
        const s = this.state.summary;
        const head = ['DATE', 'PARTICULARS', 'AMOUNT', 'PARTICULARS', 'AMOUNT'];
        const ws = XLSX.utils.aoa_to_sheet([[this.reportTitle()], head, ...this.buildLedger(), ['', '', s.totalLeft, '', s.totalRight]]);
        ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
        ws['!cols'] = [{ wch: 14 }, { wch: 28 }, { wch: 14 }, { wch: 36 }, { wch: 14 }];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Cash Summary');
        const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([buf], { type: 'application/octet-stream' }), 'Cash_Summary_Report.xlsx');
    };

    handleExportPdfAction = () => {
        if (!this.state.summary) return this.showError('No data to export');
        const s = this.state.summary;
        const fmt = (v) => (v === '' ? '' : formatNum(v));
        const body = this.buildLedger().map(r => [r[0], r[1], fmt(r[2]), r[3], fmt(r[4])]);
        body.push(['', '', formatNum(s.totalLeft), '', formatNum(s.totalRight)]);
        const doc = new jsPDF('l', 'mm', 'a4');
        doc.setFontSize(12);
        doc.text(this.reportTitle(), 14, 10);
        doc.autoTable({
            head: [['DATE', 'PARTICULARS', 'AMOUNT', 'PARTICULARS', 'AMOUNT']],
            body,
            startY: 15,
            theme: 'grid',
            styles: { fontSize: 9, cellPadding: 2, lineColor: [150, 150, 150], lineWidth: 0.2 },
            columnStyles: { 2: { halign: 'right' }, 4: { halign: 'right' } },
            headStyles: { fillColor: [244, 176, 176], textColor: 0, halign: 'center' },
            didParseCell: (d) => {
                if (d.section === 'body' && d.row.index === body.length - 1) d.cell.styles.fontStyle = 'bold';
            },
        });
        doc.save('Cash_Summary_Report.pdf');
    };

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { reportDate, searched, Restaurant, btnLoading, summary } = this.state;
        const busy = btnLoading !== null;
        const right = { textAlign: 'right' };
        const headerGroup = (
            <ColumnGroup>
                <Row>
                    <Column header={this.reportTitle()} colSpan={5} style={{ textAlign: 'center', background: '#f4b0b0', color: '#000' }} />
                </Row>
                <Row>
                    <Column header="DATE" style={{ minWidth: '120px' }} />
                    <Column header="PARTICULARS" style={{ minWidth: '220px', borderLeft: '1px solid #dee2e6' }} />
                    <Column header="AMOUNT" style={{ minWidth: '120px', ...right }} />
                    <Column header="PARTICULARS" style={{ minWidth: '280px', borderLeft: '1px solid #dee2e6' }} />
                    <Column header="AMOUNT" style={{ minWidth: '120px', ...right }} />
                </Row>
            </ColumnGroup>
        );
        const footerGroup = (
            <ColumnGroup>
                <Row>
                    <Column footer="TOTAL" colSpan={2} />
                    <Column footer={summary ? formatNum(summary.totalLeft) : ''} footerStyle={right} />
                    <Column footer="" />
                    <Column footer={summary ? formatNum(summary.totalRight) : ''} footerStyle={right} />
                </Row>
            </ColumnGroup>
        );

        return (
            <div className="p-grid padding">
                {busy && <div className="page-loader"></div>}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <CashSummaryReportHeader />

                        <div className="p-grid p-align-end">
                            <div className="p-col-12 p-md-3 p-lg-3 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Restaurant</label>
                                <Dropdown
                                    placeholder="Restaurant"
                                    name="Restaurant"
                                    options={RESTAURANTS}
                                    value={Restaurant}
                                    onChange={(e) => this.setState({ Restaurant: e.value })}
                                    filter
                                    autoWidth
                                    className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Date</label>
                                <Calendar placeholder="Date" value={reportDate} dateFormat="dd/mm/yy" onChange={(e) => this.setState({ reportDate: e.value })} />
                            </div>

                            <div className="p-col-12 p-md-7 p-lg-7 p-d-flex p-ai-end p-gap-2">
                                <Button label="Search" icon="pi pi-search" className="p-button-primary" onClick={this.handleSearch} disabled={busy} />
                                <Button label="Show All" icon="pi pi-list" className="p-button-secondary p-ml-2" onClick={this.handleShowAll} disabled={busy} />
                                <Button label="Clear" icon="pi pi-times" className="p-button-secondary p-ml-2" onClick={this.handleClear} disabled={busy} />
                            </div>

                            <div className="p-col-12 p-d-flex p-jc-end p-gap-2 p-mt-1">
                                <Button label="Export as Excel" icon="pi pi-file-excel" className="p-button-warning" onClick={this.handleExportExcel} disabled={!searched || busy} />
                                <Button label="Export as PDF" icon="pi pi-file-pdf" className="p-button-danger p-ml-2" onClick={this.handleExportPdf} disabled={!searched || busy} />
                            </div>
                        </div>

                        {searched && !summary && (
                            <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}>
                                No records found
                            </div>
                        )}

                        {searched && summary && (
                            <div className="p-col-12" style={{ marginTop: '1em', overflowX: 'auto' }}>
                                <DataTable
                                    value={this.buildLedger().map((r, i) => ({ id: i, date: r[0], lLabel: r[1], lAmt: r[2], rLabel: r[3], rAmt: r[4] }))}
                                    dataKey="id"
                                    headerColumnGroup={headerGroup}
                                    footerColumnGroup={footerGroup}
                                    scrollable
                                    scrollHeight="500px"
                                    scrollDirection="both"
                                    style={{ width: '100%' }}
                                    emptyMessage="No records found"
                                    {...DATATABLE_CONFIG}
                                >
                                    <Column field="date" style={{ minWidth: '120px' }} />
                                    <Column field="lLabel" style={{ minWidth: '220px', borderLeft: '1px solid #dee2e6' }} />
                                    <Column field="lAmt" body={(r) => formatNum(r.lAmt)} style={{ minWidth: '120px', textAlign: 'right' }} />
                                    <Column field="rLabel" style={{ minWidth: '280px', borderLeft: '1px solid #dee2e6' }} />
                                    <Column field="rAmt" body={(r) => formatNum(r.rAmt)} style={{ minWidth: '120px', textAlign: 'right' }} />
                                </DataTable>
                            </div>
                        )}

                        {!searched && (
                            <div className="p-d-flex p-jc-center p-ai-center" style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}>
                                <i className="pi pi-search p-mr-2" />
                                Select a date and click Search to view the report.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
});

export default connect(mapStateToProps)(CashSummaryReport);
